import React, { FC, useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { useParams } from 'react-router'
import { Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { toast } from 'react-toastify'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { OnboardingPasswordDto } from '@sparling/financial-common'
import { useRest } from '../../../components/Context'
import { TitleOnBoarding } from '../../../components/TitleOnBoarding'
import { CInputField } from '../../../components/CustomInputs/CInputField'
import { InputFieldPassword } from '../../../components/CustomInputs/InputFieldPassword'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { setUserForResetPassword } from '../../../services/setStepperFormData'
import { getUserForResetPassword } from '../../../services/getStepperFormData'
import { Loader } from '../../../components/Loader'

const resolver = classValidatorResolver(OnboardingPasswordDto)

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 1184,
    margin: '0 auto',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  formContainer: {
    width: '632px',
    paddingTop: theme.spacing(10),
  },
  inputWrap: {
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
}))

interface ICreateCompany {
  title: string
}

export const ResetPassword: FC<ICreateCompany> = ({ title }) => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { rxios } = useRest()
  const { uniqKey } = useParams() as { [key: string]: string }
  const [activeStep] = useState(7)
  const [loading, setLoading] = useState<boolean>(true)
  // eslint-disable-next-line
  const [data, setData] = useState<any>({})

  const userDataStorage = getUserForResetPassword()
  const formMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    defaultValues: userDataStorage,
    resolver,
  })

  const { formState, setValue } = formMethods

  const { isValid } = formState

  const getData = useCallback(async () => {
    let cleanupFunction = false
    setLoading(true)

    rxios.get(`user/onboarding/${uniqKey}`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setData(next)
        setUserForResetPassword(next)
        const { firstName, lastName, email } = next

        setValue('firstName', firstName)
        setValue('lastName', lastName)
        setValue('email', email)

        if (next.termsAccepted) {
          history.push({
            ...location,
            state: {
              activeStep: 8,
            },
          })
        }

        if (next.dataEntered && !next.termsAccepted) {
          history.push({
            ...location,
            state: {
              activeStep: 7,
            },
          })
        }

        if (!cleanupFunction) setLoading(false)
      },
      // eslint-disable-next-line
      (error: any) => {
        history.push({
          ...location,
          state: {
            activeStep: 9,
          },
        })
      },
      () => {
        setLoading(false)
      },
    )
    cleanupFunction = true
    // eslint-disable-next-line
  }, [rxios, uniqKey])

  useEffect(() => {
    ;(async () => {
      await getData()
    })()
    // eslint-disable-next-line
  }, [])

  const userOnboardingSave = useCallback(() => {
    const storage = getUserForResetPassword()
    rxios.put('/user/onboarding', { ...storage.user }).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        if (next.success) {
          history.push({
            ...location,
            state: {
              activeStep,
            },
          })
        }
      },
      (error) => {
        toast.error(error.response.data.message)
      },
    )
  }, [activeStep, history, location, rxios])

  const onSubmit = useCallback(
    (newValue) => {
      setUserForResetPassword({
        ...data,
        user: {
          hash: uniqKey,
          firstName: newValue.firstName,
          lastName: newValue.lastName,
          email: newValue.email,
          password: newValue.password,
        },
      })
      userOnboardingSave()
    },
    // eslint-disable-next-line
    [activeStep, history, location],
  )

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)} className={classes.form}>
            <Grid className={classes.container} container direction="column" alignItems="center">
              <TitleOnBoarding title={title} activeStep={activeStep} />
              <Grid container item className={classes.formContainer} direction={'column'}>
                <Grid container className={classes.inputWrap}>
                  <CInputField name={'firstName'} label={'First Name'} />
                  <CInputField name={'lastName'} label={'Last Name'} />
                </Grid>
                <Grid container className={classes.inputWrap}>
                  <CInputField name={'email'} label={'Email'} />
                </Grid>
                <Grid container className={classes.inputWrap}>
                  <InputFieldPassword name={'password'} label={'New Password'} />
                  <InputFieldPassword name={'confirmPassword'} label={'Confirm Password'} />
                </Grid>
              </Grid>
              <GreenButton disabled={!isValid} type="submit" width={320} mt={64} mb={247} text="Next" />
            </Grid>
          </form>
        </FormProvider>
      )}
    </>
  )
}
